/**
 * ProductionChart - Display oil/gas/water production over time
 * Uses recharts for responsive mobile-friendly charts
 */

import { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, ToggleButton, ToggleButtonGroup, Paper } from '@mui/material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, ComposedChart, Line
} from 'recharts';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import { getCyberColors } from '../theme/cyberColors';
import { useTranslation } from '../i18n/translations';

interface ProductionData {
  month: string;
  oil: number;
  gas: number;
  water: number;
}

interface ProductionChartProps {
  wellId: string;
  darkMode?: boolean;
  highContrast?: boolean;
  monthsBack?: number;
  height?: number;
}

type ChartType = 'area' | 'bar' | 'composed';

export function ProductionChart({ 
  wellId, 
  darkMode = true, 
  highContrast = false,
  monthsBack = 24,
  height = 300 
}: ProductionChartProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<ProductionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<ChartType>('area');
  const [totals, setTotals] = useState<{ oilBbls: number; gasMcf: number; waterBbls: number } | null>(null);
  const [wellName, setWellName] = useState<string>('');

  const cyber = getCyberColors(darkMode, highContrast);

  useEffect(() => {
    async function fetchProduction() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/wells/${encodeURIComponent(wellId)}/production?months=${monthsBack}`);
        const result = await response.json();

        if (result.error) {
          setError(result.message || result.error);
          return;
        }

        setData(result.chartData || []);
        setTotals(result.totals || null);
        setWellName(result.wellName || '');

        if (result.chartData?.length === 0) {
          setError('No production data available');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load production data');
      } finally {
        setLoading(false);
      }
    }

    if (wellId) {
      fetchProduction();
    }
  }, [wellId, monthsBack]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height, flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={40} sx={{ color: cyber.neonCyan }} />
        <Typography variant="body2" sx={{ color: cyber.textMuted }}>
          {t('loadingProductionData')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height, flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2" sx={{ color: cyber.textMuted, textAlign: 'center' }}>
          {error}
        </Typography>
        <Typography variant="caption" sx={{ color: cyber.textMuted, opacity: 0.6 }}>
          {t('productionDataUnavailable')}
        </Typography>
      </Box>
    );
  }

  // Format month labels
  const formatMonth = (month: string) => {
    const [year, m] = month.split('-');
    return `${m}/${year.slice(2)}`;
  };

  // Format large numbers
  const formatValue = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
      <Paper
        sx={{
          p: 1.5,
          background: darkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          border: `1px solid ${cyber.neonCyan}40`,
          backdropFilter: 'blur(8px)',
        }}
      >
        <Typography variant="caption" sx={{ color: cyber.textMuted, display: 'block', mb: 1 }}>
          {formatMonth(label)}
        </Typography>
        {payload.map((entry: any, index: number) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color }} />
            <Typography variant="caption" sx={{ color: cyber.textSecondary }}>
              {entry.name}: {formatValue(entry.value)} {entry.name === 'Oil' || entry.name === 'Water' ? 'bbls' : 'mcf'}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  };

  const chartColors = {
    oil: '#f59e0b',    // Amber for oil
    gas: '#ef4444',    // Red for gas (flare color)
    water: '#0ea5e9',  // Blue for water
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with stats */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 1 }}>
        <Typography variant="subtitle2" sx={{ color: cyber.textPrimary, fontWeight: 600 }}>
          {wellName ? `${wellName} Production` : 'Production History'}
        </Typography>
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={(_, val) => val && setChartType(val)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 1,
              py: 0.25,
              fontSize: '0.7rem',
              color: cyber.textMuted,
              borderColor: `${cyber.neonCyan}40`,
              '&.Mui-selected': {
                color: cyber.neonCyan,
                bgcolor: `${cyber.neonCyan}20`,
              },
            },
          }}
        >
          <ToggleButton value="area">Area</ToggleButton>
          <ToggleButton value="bar">Bar</ToggleButton>
          <ToggleButton value="composed">Mix</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Totals summary */}
      {totals && (
        <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2, px: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
              <LocalGasStationIcon sx={{ fontSize: 16, color: chartColors.oil }} />
              <Typography variant="caption" sx={{ color: chartColors.oil, fontWeight: 600 }}>
                {formatValue(totals.oilBbls)}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: cyber.textMuted, fontSize: '0.65rem' }}>
              Total Oil (bbls)
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
              <WhatshotIcon sx={{ fontSize: 16, color: chartColors.gas }} />
              <Typography variant="caption" sx={{ color: chartColors.gas, fontWeight: 600 }}>
                {formatValue(totals.gasMcf)}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: cyber.textMuted, fontSize: '0.65rem' }}>
              Total Gas (mcf)
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
              <WaterDropIcon sx={{ fontSize: 16, color: chartColors.water }} />
              <Typography variant="caption" sx={{ color: chartColors.water, fontWeight: 600 }}>
                {formatValue(totals.waterBbls)}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: cyber.textMuted, fontSize: '0.65rem' }}>
              Total Water (bbls)
            </Typography>
          </Box>
        </Box>
      )}

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        {chartType === 'area' ? (
          <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <defs>
              <linearGradient id="gradientOil" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartColors.oil} stopOpacity={0.4} />
                <stop offset="95%" stopColor={chartColors.oil} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradientGas" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartColors.gas} stopOpacity={0.4} />
                <stop offset="95%" stopColor={chartColors.gas} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradientWater" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={chartColors.water} stopOpacity={0.4} />
                <stop offset="95%" stopColor={chartColors.water} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={`${cyber.textMuted}30`} />
            <XAxis 
              dataKey="month" 
              tickFormatter={formatMonth} 
              tick={{ fontSize: 10, fill: cyber.textMuted }}
              interval="preserveStartEnd"
            />
            <YAxis 
              tickFormatter={formatValue} 
              tick={{ fontSize: 10, fill: cyber.textMuted }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '10px' }}
              formatter={(value) => <span style={{ color: cyber.textMuted }}>{value}</span>}
            />
            <Area type="monotone" dataKey="oil" name="Oil" stroke={chartColors.oil} fill="url(#gradientOil)" strokeWidth={2} />
            <Area type="monotone" dataKey="gas" name="Gas" stroke={chartColors.gas} fill="url(#gradientGas)" strokeWidth={2} />
            <Area type="monotone" dataKey="water" name="Water" stroke={chartColors.water} fill="url(#gradientWater)" strokeWidth={2} />
          </AreaChart>
        ) : chartType === 'bar' ? (
          <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={`${cyber.textMuted}30`} />
            <XAxis 
              dataKey="month" 
              tickFormatter={formatMonth} 
              tick={{ fontSize: 10, fill: cyber.textMuted }}
              interval="preserveStartEnd"
            />
            <YAxis 
              tickFormatter={formatValue} 
              tick={{ fontSize: 10, fill: cyber.textMuted }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '10px' }}
              formatter={(value) => <span style={{ color: cyber.textMuted }}>{value}</span>}
            />
            <Bar dataKey="oil" name="Oil" fill={chartColors.oil} radius={[4, 4, 0, 0]} />
            <Bar dataKey="gas" name="Gas" fill={chartColors.gas} radius={[4, 4, 0, 0]} />
            <Bar dataKey="water" name="Water" fill={chartColors.water} radius={[4, 4, 0, 0]} />
          </BarChart>
        ) : (
          <ComposedChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={`${cyber.textMuted}30`} />
            <XAxis 
              dataKey="month" 
              tickFormatter={formatMonth} 
              tick={{ fontSize: 10, fill: cyber.textMuted }}
              interval="preserveStartEnd"
            />
            <YAxis 
              tickFormatter={formatValue} 
              tick={{ fontSize: 10, fill: cyber.textMuted }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '10px' }}
              formatter={(value) => <span style={{ color: cyber.textMuted }}>{value}</span>}
            />
            <Bar dataKey="oil" name="Oil" fill={chartColors.oil} radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="gas" name="Gas" stroke={chartColors.gas} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="water" name="Water" stroke={chartColors.water} strokeWidth={2} dot={false} />
          </ComposedChart>
        )}
      </ResponsiveContainer>

      {/* Data source note */}
      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          textAlign: 'center', 
          color: cyber.textMuted, 
          opacity: 0.6,
          mt: 1,
          fontSize: '0.6rem',
        }}
      >
        Data source: NM OCD C-115 Reports • Last {monthsBack} months
      </Typography>
    </Box>
  );
}

export default ProductionChart;
